import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations as baseTranslations } from "../i18n/translations";
import { getPublicSiteTranslations } from "../lib/api";
import { useSite } from "./SiteContext";

const LanguageContext = createContext(null);
const LEGACY_AR_HERO_TITLE = "شبكة الشركاء المؤهلين لقطاع الاتصالات والبنية التحتية";
const UPDATED_AR_HERO_TITLE = "منصة تنافسية لأفضل عروض أسعار الاتصالات والبنية التحتية";
const LEGACY_EN_HERO_TITLE = "The Network of Qualified Partners for Telecom & Infrastructure";
const UPDATED_EN_HERO_TITLE = "A Competitive Platform for the Best Telecom & Infrastructure Quotations";

const deepMerge = (base, extra) => {
  if (extra === undefined) return base;
  if (Array.isArray(base) && Array.isArray(extra)) {
    const max = Math.max(base.length, extra.length);
    return Array.from({ length: max }, (_, i) => extra[i] === undefined ? base[i] : deepMerge(base[i], extra[i]));
  }
  if (!extra || typeof extra !== "object" || Array.isArray(extra)) return extra;
  const out = { ...(base || {}) };
  Object.entries(extra).forEach(([key, value]) => { out[key] = deepMerge(out[key], value); });
  return out;
};

export const LanguageProvider = ({ children }) => {
  const { settings } = useSite();
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [overrides, setOverrides] = useState({ en: {}, ar: {} });

  useEffect(() => {
    getPublicSiteTranslations().then((data) => setOverrides(data || { en: {}, ar: {} })).catch(() => {});
  }, []);

  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("lang", lang);
  }, [lang]);

  const toggleLang = () => setLang((p) => (p === "en" ? "ar" : "en"));
  const allTranslations = useMemo(() => {
    const mergedEn = deepMerge(baseTranslations.en, overrides.en || {});
    const en = mergedEn?.hero?.title === LEGACY_EN_HERO_TITLE
      ? { ...mergedEn, hero: { ...mergedEn.hero, title: UPDATED_EN_HERO_TITLE } }
      : mergedEn;
    const mergedAr = deepMerge(baseTranslations.ar, overrides.ar || {});
    const ar = mergedAr?.hero?.title === LEGACY_AR_HERO_TITLE
      ? { ...mergedAr, hero: { ...mergedAr.hero, title: UPDATED_AR_HERO_TITLE } }
      : mergedAr;
    return { en, ar };
  }, [overrides]);
  const brandName = settings?.identity?.site_name || "TowerLink";
  const replaceBrand = (value) => {
    if (typeof value === "string") {
      if (brandName !== "TowerLink" && value.includes("TowerLink") && !value.includes(brandName)) return value.replaceAll("TowerLink", brandName);
      return value;
    }
    if (Array.isArray(value)) return value.map(replaceBrand);
    if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replaceBrand(v)]));
    return value;
  };
  const t = replaceBrand(allTranslations[lang]);
  const isRTL = lang === "ar";

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t, isRTL, overrides, setOverrides }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
